'use client'

import { useState, useEffect, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/light'

import useEmblaCarousel from 'embla-carousel-react'

import ProductListing from '@/app/_components/ProductListing'
import { getRecentlyViewedProductIds } from '@/app/_lib/services/RecentlyViewedService'

const RecentlyViewedProducts = ({
    currentProductId = null,
    title = 'Recently Viewed',
    subtitle = 'Items you\'ve recently browsed'
}) => {
    const [recentlyViewed, setRecentlyViewed] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [canScrollPrev, setCanScrollPrev] = useState(false)
    const [canScrollNext, setCanScrollNext] = useState(false)

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        align: 'start',
        slidesToScroll: 2,
        containScroll: 'trimSnaps'
    })

    const updateScrollButtons = useCallback(() => {
        if (!emblaApi) return
        setCanScrollPrev(emblaApi.canScrollPrev())
        setCanScrollNext(emblaApi.canScrollNext())
    }, [emblaApi])

    useEffect(() => {
        if (!emblaApi) return
        updateScrollButtons()
        emblaApi.on('select', updateScrollButtons)
        emblaApi.on('reInit', updateScrollButtons)
        return () => {
            emblaApi.off('select', updateScrollButtons)
            emblaApi.off('reInit', updateScrollButtons)
        }
    }, [emblaApi, updateScrollButtons])

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev()
    }, [emblaApi])

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext()
    }, [emblaApi])

    useEffect(() => {
        const loadProducts = async () => {
            // Get IDs from localStorage
            let productIds = getRecentlyViewedProductIds()

            // Filter out current product if provided
            if (currentProductId) {
                productIds = productIds.filter(id => id !== currentProductId)
            }

            if (productIds.length === 0) {
                setIsLoading(false)
                return
            }

            // Fetch fresh product data via Route Handler (not Server Action)
            // to avoid bot/crawler RSC errors
            const response = await fetch('/api/products/by-ids', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sub_product_ids: productIds }),
            }).then(res => res.json()).catch(() => ({ success: false }))

            if (response.success && response.products) {
                setRecentlyViewed(response.products)
            }

            setIsLoading(false)
        }

        loadProducts()
    }, [currentProductId])

    if (isLoading || !recentlyViewed || recentlyViewed.length === 0) {
        return null
    }

    const anyOnSale = recentlyViewed.some(p => p.on_sale == 1)
    const saleSpacerClasses = 'row-has-sale-base row-has-sale-md row-has-sale-xl row-has-sale-2xl'

    return (
        <div className="site-padding mt-16 mb-16 w-full min-[1810px]:px-0 relative">
            <div className="mb-8">
                <h2 className="leading-[1] text-2xl font-extralight">{title}</h2>
                <p className="text-xs text-light-3 m-0">{subtitle}</p>
            </div>

            <div className="flex items-center -mr-3">
                {/* Left Arrow */}
                {(canScrollPrev || canScrollNext) && (
                    <button
                        onClick={scrollPrev}
                        disabled={!canScrollPrev}
                        aria-label="Previous products"
                        className={`absolute left-[calc(var(--site-padding)+0.625rem)] min-[1810px]:left-[0.625rem] top-[40%] z-40 shrink-0 ${canScrollPrev ? 'cursor-pointer' : 'cursor-default opacity-30'}`}
                    >
                        <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:shadow-lg transition-shadow">
                            <FontAwesomeIcon icon={faChevronLeft} className="text-dark text-sm" />
                        </div>
                    </button>
                )}

                {/* Carousel */}
                <div className="flex-1 min-w-0">
                    <div ref={emblaRef} className="overflow-hidden">
                        <div className="flex">
                            {recentlyViewed.map((product, index) => (
                                <div
                                    key={`${product.sub_product_id}-${index}`}
                                    className="flex-none w-1/2 sm:w-1/3 md:w-1/4 xl:w-1/5 2xl:w-[16.666%] pr-3"
                                >
                                    <ProductListing
                                        product={product}
                                        hideDetails={false}
                                        className={anyOnSale && product.on_sale != 1 ? saleSpacerClasses : ''}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Arrow */}
                {(canScrollPrev || canScrollNext) && (
                    <button
                        onClick={scrollNext}
                        disabled={!canScrollNext}
                        aria-label="Next products"
                        className={`absolute right-[calc(var(--site-padding)+0.625rem)] min-[1810px]:right-[0.625rem] top-[40%] z-40 shrink-0 ${canScrollNext ? 'cursor-pointer' : 'cursor-default opacity-30'}`}
                    >
                        <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:shadow-lg transition-shadow">
                            <FontAwesomeIcon icon={faChevronRight} className="text-dark text-sm" />
                        </div>
                    </button>
                )}
            </div>
        </div>
    )
}

export default RecentlyViewedProducts
